import React, { useState } from "react";
// UI Framework
import { Menu, MenuItem, Tooltip } from "@mui/material";
// Common Components
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { TimePicker, Modal } from "src/Components";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
import Cell from "src/Tables/TableTemplate//Cell";
// Requests
import { instrumentNavDifferenceGraph, instrumentNavComparisonGraph, instrumentNavRefitGraph, instrumentNavRefitDelayGraph } from "src/Requests/goldArbitrage";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import moment from "moment";
// Types
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IInstrumentBubbleGraph } from "src/Interfaces/goldArbitrage";

const NavDifferenceGraphUI = ({
  headerName,
  row,
  translateHeaderName,
  comparison = false,
  refit = false,
  delay = false,
}: any) => {
  // local store
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto[]>([]);
  const [formValues, setFormValues] = React.useState<IInstrumentBubbleGraph>({
    date: moment().format("YYYY-MM-DD"),
    fromTime: "11:00:00",
    toTime: "18:00:00",
    instrumentId: row.instrumentId,
  });

  const [timeISOValues, setTimeISOValues] = React.useState<{
    fromTime: string | null;
    toTime: string | null;
  }>({
    fromTime: getFromTimeISODefaultValue(),
    toTime: getToTimeISODefaultValue(),
  });
  const [submittedRequest, setSubmittedRequest] =
    React.useState<IInstrumentBubbleGraph>(formValues);

  React.useEffect(() => {
    if (!isModalOpen) return;
    let cancelled = false;
    let pending = false;

    const refresh = (background = false) => {
      if (pending) return;
      pending = true;
      if (!background) setLoading(true);
      const getGraph = delay ? instrumentNavRefitDelayGraph : refit ? instrumentNavRefitGraph
        : comparison ? instrumentNavComparisonGraph : instrumentNavDifferenceGraph;
      getGraph(submittedRequest)
        .then((resp: Graph_Dto | Graph_Dto[]) => {
          if (!cancelled) setGraphData(Array.isArray(resp) ? resp : resp ? [resp] : []);
        })
        .catch((error) => {
          if (!cancelled) packetErrorHandler(error);
        })
        .finally(() => {
          pending = false;
          if (!cancelled) setLoading(false);
        });
    };

    setGraphData([]);
    refresh();
    const timer = window.setInterval(() => {
      const now = moment();
      const end = moment(`${submittedRequest.date}T${submittedRequest.toTime}`);
      if (submittedRequest.date === now.format("YYYY-MM-DD") && end.isSameOrAfter(now)) {
        refresh(true);
      }
    }, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isModalOpen, submittedRequest, comparison, refit, delay]);

  function getFromTimeISODefaultValue() {
    const defaultFromTime = new Date();
    defaultFromTime.setHours(11, 0, 0, 0); // Set to 11:00 AM today

    return defaultFromTime.toISOString();
  }
  function getToTimeISODefaultValue() {
    const defaultToTime = new Date();
    defaultToTime.setHours(18, 0, 0, 0); // Set to 18:00 PM today
    return defaultToTime.toISOString();
  }

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const timePickerChangeHandler = (
    name: string,
    value: {
      timeString: string;
      ISOString: string | null;
    },
  ) => {
    setTimeISOValues({ ...timeISOValues, [name]: value.ISOString });
    setFormValues({ ...formValues, [name]: value.timeString });
  };

  const onSubmit = () => {
    setSubmittedRequest({ ...formValues, instrumentId: row.instrumentId });
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const getChartHandler = () => {
    onSubmit();
    handleCloseMenu();
    setIsModalOpen(true);
  };

  const ModalUI = () => {
    return (
      <>
        <div className="h-full w-full min-h-0 flex flex-row items-stretch gap-5 px-4">
          {loading ? <FullScreenLoading /> : <></>}
          <div className="flex flex-col justify-start items-start gap-3">
            <DatePicker
              label="تاریخ"
              name="date"
              onChange={changeHandler}
              value={formValues.date}
            />
            <TimePicker
              name="fromTime"
              value={timeISOValues.fromTime}
              onChange={timePickerChangeHandler}
              placeholder="از ساعت"
            />
            <TimePicker
              name="toTime"
              value={timeISOValues.toTime}
              onChange={timePickerChangeHandler}
              placeholder="تا ساعت"
            />
            <Button text="جستجو" onClick={onSubmit} variant="contained" />
          </div>
          <div className="h-full w-full min-h-0 flex flex-col justify-start items-stretch gap-2">
            <div className="text-lg font-bold text-right shrink-0">
              {row.name}
              {delay ? " — تأخیر محاسباتی میان‌روز (ثانیه)" : refit ? " — NAV محاسباتی ۲ / Refit 1min" : comparison ? " — مقایسه NAVهای رکوردشده" : ""}
            </div>
            <div className="flex-1 min-h-0 w-full mx-auto">
              <WrappedTimeSeriesChart
                dataset={graphData}
                YValueType="priceWithDecimal"
                showFullDateTime
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={row.name || translateHeaderName || headerName}
          UI={ModalUI()}
          modalBoxClassName="!z-[9999]"
        />
      ) : (
        <></>
      )}
      <Tooltip
        title={comparison ? "" : "نمودار"}
        className="!w-full !relative"
        open={!comparison && isTooltipOpen}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        <div onContextMenu={rightClickHandler} className="ltr">
          <Cell clonedRow={row} headerName={headerName} type={comparison ? "HoverTable" : "Price"} />
          {isMenuOpen ? (
            <Menu
              className="!z-[9998]"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
            </Menu>
          ) : (
            <></>
          )}
        </div>
      </Tooltip>
    </>
  );
};

export default NavDifferenceGraphUI;
